import {Paragraph} from "@/pages/common/Home/Dissection/Article";

import salesforce_20_1 from "@images/home/analytics/top_priority/article_2_salesforce-20per-1.svg";
import salesforce_20_2 from "@images/home/analytics/top_priority/article_2_salesforce-20per-2.svg";
import salesforce_60 from "@images/home/analytics/top_priority/article_2_salesforce-60per.svg";
import salesforce_chart from "@images/home/analytics/top_priority/article_2_salesforce-chart.png";
import priority_icon_1 from "@images/home/analytics/top_priority/article_2_top-priority_icon-1.png";
import priority_icon_2 from "@images/home/analytics/top_priority/article_2_top-priority_icon-2.png";
import priority_icon_3 from "@images/home/analytics/top_priority/article_2_top-priority_icon-3.png";
import priority_icon_4 from "@images/home/analytics/top_priority/article_2_top-priority_icon-4.png";
import triple_20 from "@images/home/analytics/top_priority/article_2_triple-20per.svg";
import triple_60 from "@images/home/analytics/top_priority/article_2_triple-60per.svg";
import triple_84 from "@images/home/analytics/top_priority/article_2_triple-84per.svg";
import triple_potencial from "@images/home/analytics/top_priority/article_2_triple-potencial.svg";
import triple_quota from "@images/home/analytics/top_priority/article_2_triple-quota.svg";
import triple_time from "@images/home/analytics/top_priority/article_2_triple-time.svg";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {H2} from "@ui/titles/H2";
import {H3} from "@ui/titles/H3";
import {useSelector} from "react-redux";

import styles from "../styles.module.scss";

export const ArticleTopPriority = () => {
  const {isMobile} = useSelector((state) => state.common.settings);

  return (
    <>
      <Paragraph>
        <H2>
          <span>
            The Top Priority for Sales Leaders: Improving Sales Enablement and
            Training in&nbsp;Pharma&nbsp;&mdash; with AVA
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Fresh into 2025, the pharmaceutical sales landscape is&nbsp;facing
              pressing challenges, highlighted by&nbsp;findings in&nbsp;the 2024
              Salesforce State of&nbsp;Sales Report (V6). The globally respected
              report (which surveyed over 5,500 sales professionals) highlighted
              one key insight that resonates deeply in&nbsp;the pharma sector:
            </span>

            <span>
              <b style={{fontSize: "23rem"}}>
                &laquo;The top priority for sales leaders is&nbsp;improving
                sales enablement and training.&raquo;
              </b>
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_text_image}>
          <div style={{paddingLeft: isMobile ? "0" : "120rem"}}>
            <div className={styles.article_text_card}>
              <ul className={styles.article_top_priority_list}>
                <li>
                  <img
                    src={priority_icon_1}
                    alt={"icon"}
                  />

                  <div>
                    <H2>
                      <span>84%</span>
                    </H2>

                    <RootDesc>
                      <span>Sales reps missing quotas</span>
                    </RootDesc>
                  </div>
                </li>

                <li>
                  <img
                    src={priority_icon_2}
                    alt={"icon"}
                  />

                  <div>
                    <H2>
                      <span>2.1x outperform</span>
                    </H2>

                    <RootDesc>
                      <span>Top-performing sales reps</span>
                    </RootDesc>
                  </div>
                </li>

                <li>
                  <img
                    src={priority_icon_3}
                    alt={"icon"}
                  />

                  <div>
                    <H2>
                      <span>30 minutes</span>
                    </H2>

                    <RootDesc>
                      <span>Weekly coaching time per manager</span>
                    </RootDesc>
                  </div>
                </li>

                <li>
                  <img
                    src={priority_icon_4}
                    alt={"icon"}
                  />

                  <div>
                    <H2>
                      <span>11.2 months</span>
                    </H2>

                    <RootDesc>
                      <span>New reps ramp-up time</span>
                    </RootDesc>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <RootDesc>
            <span>
              The statistics are compelling: 84% of&nbsp;sales reps missed their
              quotas in&nbsp;the past year, while high-performing reps
              outperform average ones by&nbsp;2.1x. Onboarding remains
              a&nbsp;critical pain point, with the average time
              to&nbsp;quota-capable status at&nbsp;11.2 months, often leaving
              sales reps struggling during their first sales cycle. Meanwhile,
              managerial bandwidth is&nbsp;stretched thin, with 47%
              of&nbsp;managers dedicating just 30 minutes per week
              to&nbsp;coaching their teams.
            </span>

            <span>
              Pharmaceutical companies are under immense pressure
              to&nbsp;address these issues-bridging the skills gap, improving
              ramp times, and fostering a&nbsp;consistent connection between
              sales and marketing teams.
            </span>

            <span>
              Enter AVA, a&nbsp;transformative AI-driven training and enablement
              tool designed to&nbsp;reshape the performance of&nbsp;pharma sales
              teams.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The Salesforce Findings That Pharma Leaders Cannot Ignore</span>
        </H2>

        <div className={styles.article_text_image}>
          <div
            style={{
              paddingLeft: isMobile ? "0" : "120rem",
              width: isMobile ? "100%" : "580rem",
            }}
          >
            <RootDesc>
              <span>
                The Salesforce report identified several critical challenges for
                sales organisations:
              </span>

              <ol>
                <li>
                  <b>Quota Attainment Crisis:</b> A&nbsp;staggering 84%
                  of&nbsp;sales reps miss their targets, underscoring the urgent
                  need for smarter training solutions.
                </li>

                <li>
                  <b>Uneven Performance Distribution:</b> 20% of&nbsp;sales reps
                  carry the team, 20% are underperforming, and 60% have untapped
                  potential that can be&nbsp;realised with effective training.
                </li>

                <li>
                  <b>Managerial Overload:</b> Managers spend only 30 minutes per
                  week coaching reps, leaving significant gaps in&nbsp;the
                  support structure for improving rep performance.
                </li>

                <li>
                  <b>Ramp Time Bottleneck:</b> The onboarding process
                  is&nbsp;drawn out, with an&nbsp;average ramp time of&nbsp;11.2
                  months&nbsp;&mdash; a&nbsp;delay that stifles revenue and
                  leaves new reps vulnerable during their initial sales cycles.
                </li>
              </ol>
            </RootDesc>
          </div>

          <div className={styles.article_text_card}>
            <div className={styles.article_salesforce}>
              <H3>
                <span>Uneven Performance Distribution</span>
              </H3>

              <hr />

              <img
                style={{
                  width: isMobile ? "170rem" : "240rem",
                  margin: "0 auto",
                }}
                src={salesforce_chart}
                alt={"chart"}
              />

              <div className={styles.article_salesforce_chart_title}>
                <RootDesc>
                  <b>
                    Uneven <br /> Performance
                  </b>
                </RootDesc>
              </div>

              <div className={styles.article_salesforce_percent_1}>
                <img
                  src={salesforce_20_1}
                  alt={"20%"}
                />
                <RootDesc>
                  <span>Underperforming</span>
                </RootDesc>
              </div>

              <div className={styles.article_salesforce_percent_2}>
                <img
                  src={salesforce_20_2}
                  alt={"20%"}
                />
                <RootDesc>
                  <span>Carry the team</span>
                </RootDesc>
              </div>

              <div className={styles.article_salesforce_percent_3}>
                <img
                  src={salesforce_60}
                  alt={"60%"}
                />
                <RootDesc>
                  <span>
                    Have untapped <br /> potential
                  </span>
                </RootDesc>
              </div>
            </div>
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_triple_cards}>
          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(90deg, #e9effa 0%, #f3eaf8 46.5%, #fde5f6 100%)`,
            }}
          >
            <img
              style={{width: "219rem"}}
              src={triple_84}
              alt={"chart"}
            />

            <H3>
              <span>Quota Attainment Crisis</span>
            </H3>

            <RootDesc>
              <span>
                A&nbsp;staggering 84% of&nbsp;sales reps miss their targets,
                underscoring the urgent need for smarter training solutions
              </span>
            </RootDesc>
          </div>

          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(90deg, #e9effa 0%, #f3eaf8 46.5%, #fde5f6 100%)`,
            }}
          >
            <img
              style={{width: "219rem"}}
              src={triple_20}
              alt={"chart"}
            />

            <H3>
              <span>Uneven Performance Distribution</span>
            </H3>

            <RootDesc>
              <span>
                20% of&nbsp;sales reps&nbsp;&mdash; carry the
                team20%&nbsp;&mdash; underperforming
              </span>
            </RootDesc>
          </div>

          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(90deg, #e9effa 0%, #f3eaf8 46.5%, #fde5f6 100%)`,
            }}
          >
            <img
              style={{width: "219rem"}}
              src={triple_60}
              alt={"chart"}
            />

            <H3>
              <span>Request training</span>
            </H3>

            <RootDesc>
              <span>
                60% of&nbsp;sales reps have untapped potential that can
                be&nbsp;realised with effective training
              </span>
            </RootDesc>
          </div>
        </div>

        <div style={{paddingLeft: isMobile ? "0" : "120rem"}}>
          <RootDesc>
            <span>
              These issues are not insurmountable but addressing them requires
              a&nbsp;radical shift in&nbsp;how training and sales enablement are
              delivered. For pharmaceutical companies, this means equipping
              sales reps with tools and strategies that align with real-world
              complexities&nbsp;&mdash; something AVA excels at.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>
            The Role of&nbsp;AVA in&nbsp;Sales Enablement and Training
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              AVA is&nbsp;more than just a&nbsp;training simulation&nbsp;&mdash;
              it’s&nbsp;a&nbsp;dynamic, advanced, GenAI-powered solution
              designed to&nbsp;meet the unique needs of&nbsp;pharma sales teams.
            </span>

            <H3>
              <span>Bridging the Sales-Marketing Divide</span>
            </H3>

            <span>
              In&nbsp;the pharmaceutical industry, a&nbsp;disconnect between
              sales and marketing teams often leads to&nbsp;misaligned
              messaging. AVA’s&nbsp;Training Library eliminates this gap,
              ensuring reps receive consistent, up-to-date product knowledge and
              marketing strategies. By&nbsp;enabling marketers to&nbsp;directly
              upload training materials, sales reps can confidently deliver
              accurate and impactful messaging in&nbsp;a&nbsp;scalable and
              efficient manner.
            </span>

            <H3>
              <span>Personalised Learning and Performance Validation</span>
            </H3>

            <span>
              Unlike traditional training methods, AVA offers a&nbsp;tailored
              learning experience for every sales rep. Whether the rep
              is&nbsp;a&nbsp;novice building foundational skills
              or&nbsp;a&nbsp;seasoned performer seeking advanced coaching, AVA
              adapts to&nbsp;individual needs. This personalised approach
              ensures that every rep progresses at&nbsp;their own pace while
              meeting performance benchmarks.
            </span>

            <span>
              More importantly, AVA validates learning through real-time and
              continuous assessments, giving both reps and managers clear
              insights into progress. This isn’t&nbsp;about checking
              boxes&nbsp;&mdash; it’s&nbsp;about ensuring reps truly master the
              skills they need to&nbsp;succeed.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Accelerating Ramp Time and Enhancing Managerial Support</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Ramp time is&nbsp;one of&nbsp;the biggest hurdles for pharma sales
              teams. The 11.2 months it&nbsp;takes for a&nbsp;new rep
              to&nbsp;become quota-capable is&nbsp;a&nbsp;heavy burden
              on&nbsp;resources, and managers, who are already stretched thin,
              cannot singlehandedly address this gap.
            </span>

            <span>
              AVA changes the game by&nbsp;providing on-demand, AI-driven
              training materials, enabling reps to&nbsp;hit the ground running.
              Through customisable simulations and roleplays, new reps gain
              practical experience from day one in&nbsp;a&nbsp;risk-free
              environment, reducing ramp times and improving confidence
              as&nbsp;they tackle their first sales cycles.
            </span>

            <span>
              At&nbsp;the same time, AVA alleviates the burden on&nbsp;managers
              by&nbsp;providing them with actionable insights. Detailed
              performance analytics help managers identify specific skill gaps,
              allowing them to&nbsp;deliver targeted coaching instead
              of&nbsp;relying on&nbsp;generic feedback.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Transforming Pharma Sales Teams</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Pharmaceutical sales leaders can no&nbsp;longer afford to&nbsp;let
              outdated training methods and misaligned priorities hold their
              teams back. With AVA, the transformation is&nbsp;tangible:
            </span>

            <ul>
              <li>
                <b>Reduced Ramp Time:</b> Equip new hires with the skills they
                need, cutting the 11.2-month average significantly.
              </li>

              <li>
                <b>Higher Quota Attainment:</b> Address the 84% failure rate
                by&nbsp;empowering reps with tools that elevate their skills and
                confidence.
              </li>

              <li>
                <b>Improved Manager Impact:</b> Help managers make every
                coaching minute count with data-driven insights and prioritised
                interventions.
              </li>

              <li>
                <b>Realised Potential:</b> Turn the 60% of&nbsp;reps with
                untapped potential into high performers, narrowing the gap
                between average and top-tier outcomes.
              </li>
            </ul>
          </RootDesc>
        </div>

        <div className={styles.article_triple_cards}>
          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(135deg, #8692CB 0%, #B262C9 100%)`,
            }}
          >
            <H3>
              <span style={{color: "var(--white)"}}>Reduced Ramp Time</span>
            </H3>

            <img
              style={{width: "120rem"}}
              src={triple_time}
              alt={"chart"}
            />

            <H2>
              <span>
                <strong>11.2</strong> months
              </span>
            </H2>

            <RootDesc>
              <span style={{color: "var(--greyLight)"}}>
                Equip new hires with the skills they need, cutting the
                11.2-months average significantly
              </span>
            </RootDesc>
          </div>

          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(135deg, #8692CB 0%, #B262C9 100%)`,
            }}
          >
            <H3>
              <span style={{color: "var(--white)"}}>
                Higher Quota Attainment
              </span>
            </H3>

            <img
              style={{width: "120rem"}}
              src={triple_quota}
              alt={"chart"}
            />

            <H2>
              <span>
                <strong>84%</strong>
              </span>
            </H2>

            <RootDesc>
              <span style={{color: "var(--greyLight)"}}>
                Address the 84% failure rate by&nbsp;empowering reps with tools
                that elevate their skills and confidence
              </span>
            </RootDesc>
          </div>

          <div
            className={styles.article_triple_card}
            style={{
              background: `linear-gradient(135deg, #8692CB 0%, #B262C9 100%)`,
            }}
          >
            <H3>
              <span style={{color: "var(--white)"}}>Realised Potential</span>
            </H3>

            <img
              style={{width: "120rem"}}
              src={triple_potencial}
              alt={"chart"}
            />

            <H2>
              <span>
                <strong>60%</strong>
              </span>
            </H2>

            <RootDesc>
              <span style={{color: "var(--greyLight)"}}>
                Turn the 60% of&nbsp;reps with untapped potential nto high
                performers, narrowing the gap between average and top-tier
                outcomes
              </span>
            </RootDesc>
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The Future of&nbsp;Pharma Sales Enablement</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              The Salesforce State of&nbsp;Sales Report makes one thing clear:
              without meaningful investment in&nbsp;sales enablement and
              training, pharmaceutical companies risk falling behind.
              High-performing reps don’t&nbsp;just happen&nbsp;&mdash; they are
              developed through consistent, personalised training and strong
              support systems.
            </span>

            <span>
              With AVA, pharmaceutical companies can achieve these goals.
              By&nbsp;addressing key challenges like managerial bandwidth, ramp
              times, and quota attainment, AVA positions sales teams
              to&nbsp;excel.
            </span>

            <span>
              For sales leaders who want to&nbsp;act on&nbsp;the insights from
              the Salesforce report, the message is&nbsp;simple: embrace
              advanced tools like AVA and take the first step toward a&nbsp;more
              empowered, high-performing salesforce.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_citations}>
          <h6>References</h6>

          <RootDesc>
            <ol>
              <li>
                <span>
                  Salesforce (2024). State of&nbsp;Sales Report&nbsp;&mdash;
                  Sixth Edition.
                </span>
              </li>

              <li>
                <span>
                  Salesforce (2024). AI-Driven Sales Performance Statistics.
                </span>
              </li>

              <li>
                <span>
                  Gong Labs (2024). Insights on&nbsp;Sales Enablement and Quota
                  Attainment.
                </span>
              </li>

              <li>
                <span>Vention (2024). Key Sales Metrics for 2024.</span>
              </li>
            </ol>
          </RootDesc>
        </div>
      </Paragraph>
    </>
  );
};
